import { ToDo } from "../../Components/ToDo";
import { Step } from "../Step/Step";
import { Cash } from "../Step/Cash";
import { StepEvent, StepEventType } from "../Step/StepEvent";
import { StepType } from "../Step/StepType";
import { TimeUtils } from "../../Utils/TimeUtils";
import { TrainingStepState } from "./Models/TrainingStepState";
import { TrainingStepData } from "./Models/TrainingStepData";
import { Line, MultilineInput, Switch } from "@tblabs/truffle";
import { Order } from "../../Models/Order";
import { DateTimeProvider } from "../../Services/DateTimeProvider";
import { appendable } from "@tblabs/truffle/Core/appendable";
import { IAccessData, TXv4AccessData } from "./AccessData/TXv4AccessData";
import { TXv4AccessInfoBox } from "./AccessData/TXv4AccessInfoBox";
import { InstructionsList } from "./Components/InstructionsList";
import { DateTimeEditInput } from "../../Components/Utils/DateSlider";


export class TrainingStep extends Step<TrainingStepState>
{
    public Type = StepType.Training;
    public Icon = "🎓";
    public TitleForCustomer = () => `Szkolenie`;
    public Title = () => `Szkolenie`;

    public InitialState = TrainingStepState.NotPlannedYet;
    public WellDoneStates = [TrainingStepState.Done, TrainingStepState.Self, TrainingStepState.Closed];
    public FailedDoneStates = [TrainingStepState.Cancelled, TrainingStepState.Undone]; // Undone is LEGACY

    public get AsEvents(): StepEvent[]
    {
        return [
            new StepEvent(StepEventType.Training, this.Order.Id, "🎓", "Szkolenie", this.Data.PlannedTime.value, this.IsDone)
        ];
    }
    public get Mana(): Cash[]
    {
        return [];
    }
    public get WhenPlanned(): Date
    {
        return this.Data.PlannedTime.value;
    }
    public get WhenDone(): Date
    {
        return this.Data.PlannedTime.value;
    }

    constructor(public Data: TrainingStepData)
    {
        super(Data);
    }

    public Init(): void
    {
        if (this.Order?.BasketItems?.some(x => x.AccessData.value))
        {
            this.GoTo(TrainingStepState.Self);
        }

        if (this.Order.IsDone)
        {
            this.GoTo(TrainingStepState.Closed);
        }
    }

    public STATES = {
        [TrainingStepState.NotPlannedYet]: {
            StateName: "Nie zaplanowane",
            Customer: {
                Content: () => [`Termin szkolenia nie został jeszcze ustalony.\nJeśli zbliża się termin, możesz do nas zadzwonić i ustalić dogodny czas`],
            },
            Admin: {
                Content: () => [
                    `Czas szkolenia nie został jeszcze ustalony`,
                    new ToDo(
                        `Ustal termin szkolenia:\n\n`,
                        new DateTimeEditInput(this.Data.PlannedTime))
                ],
                Actions: {
                    "✅": () => this.GoTo(TrainingStepState.Planning),
                    "❌": () => this.GoTo(TrainingStepState.Canceling),
                },
            },
        },
        [TrainingStepState.Planning]: {
            StateName: "Planowanie w toku",
            Customer: {
                Content: () => [`🕓 Szkolenie jest właśnie planowane...`],
            },
            Admin: {
                Content: () => [
                    new ToDo(
                        `Ustal termin szkolenia:\n\n`,
                        new DateTimeEditInput(this.Data.PlannedTime))],
                Actions: {
                    "✅": () => this.GoTo(TrainingStepState.Planned),
                    "❌": () => this.GoTo(TrainingStepState.NotPlannedYet),
                },
            },
        },
        [TrainingStepState.Planned]: {
            StateName: "Zaplanowane",
            Timeout: () => this.Data.PlannedTime,
            Customer: {
                Content: () => [
                    `Szkolenie wstępnie zaplanowano na ${TimeUtils.FormattedDate(this.Data.PlannedTime)}. Odbędzie się przez telefon i potrwa około 10 minut`,
                    new Line(),
                    `Możesz wstępnie zapoznać się z materiałami szkoleniowymi:`,
                    new InstructionsList(this.Order),
                    new Line(),
                    `Jeśli masz już sprzęt: zadzwoń. Im wcześniej go uruchomimy tym lepiej!`
                ],
            },
            Admin: {
                Content: () => [
                    `Szkolenie zaplanowano na:\n${TimeUtils.FormattedDate(this.Data.PlannedTime)}`,
                    new ToDo(`Jeśli szkolenie się odbyło - kliknij ✅\nJeśli zostało odwołane - kliknij ❌ i podaj powód odwołania`)
                ],
                Actions: {
                    "✅": () => { this.Data.DoneTime.value = DateTimeProvider.Now; this.GoTo(TrainingStepState.Done); },
                    "❌": () => this.GoTo(TrainingStepState.Canceling),
                },
            },
        },
        [TrainingStepState.Self]: {
            StateName: "Samoszkolenie",
            Timeout: () => this.Data.PlannedTime,
            Customer: {
                Content: () => [
                    `Zapoznaj się z materiałami szkoleniowymi:`,
                    new InstructionsList(this.Order),
                    ...this.AccessInfo(this.Order),
                ],
            },
            Admin: {
                Content: () => [
                    `Klient otrzymał materiały do samoszkolenia dla ${this.DevicesIds(this.Order).join(", ") || "(brak id urządzeń)"}`,
                    new ToDo(`Jeśli szkolenie się odbyło - kliknij ✅\nJeśli zostało odwołane - kliknij ❌ i podaj powód odwołania`)
                ],
                Actions: {
                    "✅": () => { this.Data.DoneTime.value = DateTimeProvider.Now; this.GoTo(TrainingStepState.Done); },
                    "❌": () => this.GoTo(TrainingStepState.Canceling),
                },
            },
        },
        [TrainingStepState.Done]: {
            StateName: "Wykonane",
            Customer: {
                Content: () => [
                    `✅ Szkolenie zakończone`,
                    new Line(),
                    `Materiały szkoleniowe odnajdziesz tutaj:`,
                    new InstructionsList(this.Order),
                    ...this.AccessInfo(this.Order),
                ],
            },
            Admin: {
                Content: () => [`✅ Szkolenie zakończone`],
            },
        },
        [TrainingStepState.Canceling]: {
            StateName: "Odwoływanie w toku",
            Customer: {
                Content: () => [
                    `✖️ Szkolenie jest właśnie odwoływane...`,
                ],
            },
            Admin: {
                Content: () => [
                    new ToDo(
                        `Podaj powód odwołania szkolenia (widoczny dla klienta!)`,
                        new MultilineInput(this.Data.CancelationReason).Placeholder("Powód odwołania szkolenia").Rows(5),
                        `\nlub wróć do planowania szkolenia`
                    )],
                Actions: {
                    "✅": () => this.GoTo(TrainingStepState.Cancelled),
                    "✖️": () => this.GoTo(TrainingStepState.NotPlannedYet),
                },
            },
        },
        [TrainingStepState.Undone]: { // [LEGACY]
            StateName: "Nie wykonane",
            Customer: {
                Content: () => [`❌ Szkolenie zostało odwołane`],
            },
            Admin: {
                Content: () => [`❌ Szkolenie zostało odwołane z powodu: ${this.Data.CancelationReason.value || "nie podano"}`],
            },
        },
        [TrainingStepState.Cancelled]: {
            StateName: "Odwołane",
            Customer: {
                Content: () => [`❌ Szkolenie zostało odwołane z powodu: ${this.Data.CancelationReason.value || "nie podano"}`],
            },
            Admin: {
                Content: () => [`❌ Szkolenie zostało odwołane z powodu: ${this.Data.CancelationReason.value || "nie podano"}`],
            },
        },
        [TrainingStepState.Closed]: {
            StateName: "Szkolenie zamknięte",
            Customer: {
                Content: () => [`✅ Szkolenie zostało zakończone`],
            },
            Admin: {
                Content: () => [`✅ Szkolenie zostało zakończone`],
            },
        },
    };

    private SingleAccessInfo(data: IAccessData): appendable
    {
        return new Switch(data.Access)
            .Case("TX-Watcher", () => new TXv4AccessInfoBox(data as TXv4AccessData))
            .Default(() => "")
            .Execute() as appendable;
    }

    private AccessInfo(order: Order): appendable[]
    {
        return order.BasketItems
            .filter(x => x.AccessData.value)
            .flatMap(x => 
            {
                const data = JSON.parse(x.AccessData.value) as IAccessData;

                if (Array.isArray(data))
                {
                    return data.map(x => this.SingleAccessInfo(x));
                }

                return this.SingleAccessInfo(data);
            });
    }

    private DevicesIds(order: Order): string[]
    {
        return order.BasketItems
            .filter(x => x.AccessData.value)
            .flatMap(x => 
            {
                const data = JSON.parse(x.AccessData.value) as IAccessData;

                return new Switch(data.Access)
                    .Case("TX-Watcher", () => (data as TXv4AccessData).DeviceId)
                    .Default(() => "")
                    .Execute() as string[];
            }) ?? [];
    }
}
